import React from 'react';
import {
  VARIANT_FIELDS, COMMON_SPEC_FIELDS,
  badgeSrc, badgeExists, badgeSortKey, ACCESSORY_BADGES, ACCESSORY_BADGE_LABELS, LPA_LOGO_B64,
} from '../badges';
import { detailEntries, sheetHasPage2 } from '../datasheet';
import { DOCUMENT_DISCLAIMER } from '../documentBranding';

const LOGO = 'data:image/png;base64,' + LPA_LOGO_B64;

export default function DatasheetPreview({ sheet, pageId, overlay, overlay2 }) {
  const s = sheet || {};
  const badges = s.badges || {};
  const allActive = Object.keys(badges).filter(k => badges[k] && badgeExists(k));
  // Strip badges render in the fixed category order (Compliance → … → Warranty).
  const activeBadges = allActive.filter(k => ACCESSORY_BADGES.indexOf(k) < 0).sort((a, b) => badgeSortKey(a) - badgeSortKey(b));
  // Accessory-type badges are shown inside the Accessories section instead.
  const accessoryBadges = ACCESSORY_BADGES.filter(k => allActive.indexOf(k) >= 0);
  const detailList = detailEntries(s)
    .map(e => e.type === 'preset'
      ? { label: e.field.label, value: (((s.productDetails || {})[e.field.id]) || '').toString().trim() }
      : { label: (e.row.label || '').trim(), value: ((e.row.value || '') + '').trim() })
    .filter(d => d.label && d.value);

  const variants = s.variants || [];
  const customCols = s.variantColumns || [];
  const colHasData = (id) => variants.some(v => (((v[id]) || '') + '').trim() !== '');
  const allCols = [...VARIANT_FIELDS, ...customCols].filter(f => colHasData(f.id));
  const beamColsAll = s.beamCols || [];
  const beamCols = beamColsAll.filter(rc => variants.some(v => ((((v.beamLumens || {})[rc.name]) || '') + '').trim() !== ''));
  const hasBeam = beamCols.length > 0;
  const ctx = (s.lumensContext || '').trim();
  const ctxSub = ctx ? <span className="ds-sub">({ctx})</span> : null;

  const cells = [];
  COMMON_SPEC_FIELDS.forEach(f => { const v = (s.commonSpecs || {})[f.id]; if (v && v.trim()) cells.push({ label: f.label, value: v }); });
  (s.commonSpecsCustom || []).forEach(c => { if (c && (c.label || '').trim() && (c.value || '').trim()) cells.push({ label: c.label.trim(), value: c.value }); });
  const commonRows = [];
  for (let i = 0; i < cells.length; i += 8) commonRows.push(cells.slice(i, i + 8));

  const bezel = (s.bezelOptions || []).filter(b => b && (b.image || (b.name || '').trim()));
  const accessories = (s.accessories || []).filter(a => a && (a.image || (a.name || '').trim() || (a.code || '').trim()));
  const photos = (s.photometrics || []).filter(p => p && (p.image || (p.label || '').trim()));
  const hasAccessorySection = accessories.length > 0 || accessoryBadges.length > 0;
  const hasPage2 = sheetHasPage2(s);
  const cornerText = (s.corner || '').trim();
  const cornerBox = (
    <div className={'ds-corner-box' + (cornerText ? '' : ' empty') + (cornerText.length > 12 ? ' long' : '')}>{cornerText}</div>
  );

  return (
    <>
      <div className="ds-page" id={pageId} data-testid="datasheet-preview">
        {cornerBox}
        <div className="ds-top">
          <div className={'ds-product-image' + (s.productImage ? '' : ' empty')}>{s.productImage ? <img src={s.productImage} alt="" /> : '(product image)'}</div>
          <div className="ds-product-head">
            <h1>{s.name || ''}{s.variant ? <span className="variant"> ({s.variant})</span> : null}</h1>
            <div className={'ds-product-code' + ((s.code || '').trim() ? '' : ' empty')}>{(s.code || '').trim()}</div>
            <h2>PRODUCT DETAILS</h2>
            <dl className="ds-details">
              {detailList.map((d, i) => <React.Fragment key={i}><dt>{d.label}:</dt><dd>{d.value}</dd></React.Fragment>)}
            </dl>
          </div>
        </div>

        <div className="ds-middle">
          <div className="ds-dim-block">
            <div className="ds-dim-label">Dimensions</div>
            <div className={'ds-dim-image' + (s.dimensionsImage ? '' : ' empty')}>{s.dimensionsImage ? <img src={s.dimensionsImage} alt="" /> : '(dimensions drawing)'}</div>
          </div>
          <div className="ds-mid-right">
            <div className="ds-badges">{activeBadges.map(k => <div className="ds-badge" key={k}><img src={badgeSrc(k)} alt={k} /></div>)}</div>
            {s.extraImage ? <div className="ds-extra-image" data-testid="ds-extra-image"><img src={s.extraImage} alt="" /></div> : null}
          </div>
        </div>

        <div className="ds-tables">
          {variants.length > 0 && allCols.length > 0 && (
            <table className="ds-table" data-testid="ds-variant-table">
              <thead>
                {hasBeam ? (
                  <>
                    <tr className="group-row">
                      {allCols.map(f => <th key={f.id} rowSpan={2}>{f.label}{f.id === 'sourceLumens' ? ctxSub : null}</th>)}
                      <th colSpan={beamCols.length}>System Lumen (lm) / Beam Angle{ctxSub}</th>
                    </tr>
                    <tr className="subhead-row">{beamCols.map((rc, i) => <th key={i}>{rc.name}{rc.angle ? <span className="ds-sub">({rc.angle})</span> : null}</th>)}</tr>
                  </>
                ) : (
                  <tr className="group-row">{allCols.map(f => <th key={f.id}>{f.label}{f.id === 'sourceLumens' ? ctxSub : null}</th>)}</tr>
                )}
              </thead>
              <tbody>
                {variants.map((v, i) => (
                  <tr key={i}>
                    {allCols.map(f => <td key={f.id}>{v[f.id] || ''}</td>)}
                    {hasBeam && beamCols.map((rc, j) => <td key={j}>{(v.beamLumens || {})[rc.name] || ''}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="ds-common">
          <div className="ds-common-tables">
            {commonRows.map((row, ri) => (
              <table className="ds-common-table" key={ri}>
                <thead><tr>{row.map((c, ci) => <th key={ci}>{c.label}</th>)}</tr></thead>
                <tbody><tr>{row.map((c, ci) => <td key={ci}>{c.value}</td>)}</tr></tbody>
              </table>
            ))}
          </div>
        </div>

        <Footer page={1} />
        {overlay || null}
      </div>

      {hasPage2 && (
        <div className="ds-page" id={pageId ? pageId + '-p2' : undefined} data-testid="datasheet-preview-p2">
          {cornerBox}
          {bezel.length > 0 && (
            <div>
              <div className="ds-section-head"><h2>Bezel</h2><div className="sub"><em>(available on request)</em></div></div>
              <div className="ds-bezel-row">
                {bezel.map((b, i) => (
                  <div className="ds-bezel-item" key={i}>
                    <div className={'img' + (b.image ? '' : ' empty')}>{b.image ? <img src={b.image} alt="" /> : '(image)'}</div>
                    <div className="name">{b.name || ''}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {hasAccessorySection && (
            <div>
              <div className="ds-section-head"><h2>Accessories</h2><div className="sub"><em>(available on request)</em></div></div>
              <div className="ds-accessory-row">
                {accessories.map((a, i) => (
                  <div className="ds-accessory-item" key={i}>
                    <div className={'img' + (a.image ? '' : ' empty')}>{a.image ? <img src={a.image} alt="" /> : '(image)'}</div>
                    <div className="name">{a.name || ''}</div>
                    {a.code ? <div className="code">{a.code}</div> : null}
                  </div>
                ))}
                {accessoryBadges.map(k => (
                  <div className="ds-accessory-item" key={'b' + k}>
                    <div className="img"><img src={badgeSrc(k)} alt={k} /></div>
                    <div className="name">{ACCESSORY_BADGE_LABELS[k] || k}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {photos.length > 0 && (
            <div>
              <div className="ds-section-head"><h2>Photometrics</h2><div className="sub"><em>{s.photoSubtitle || ''}</em></div></div>
              <div className={'ds-photo-grid' + (hasAccessorySection || bezel.length > 0 ? ' compact' : '')}>
                {photos.map((p, i) => (
                  <div className="ds-photo-item" key={i}>
                    <div className="label">{p.label || ''}</div>
                    <div className={'img' + (p.image ? '' : ' empty')}>{p.image ? <img src={p.image} alt="" /> : '(image)'}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <Footer page={2} />
          {overlay2 || null}
        </div>
      )}
    </>
  );
}

function Footer({ page }) {
  return (
    <div className="ds-footer" data-testid={'au-footer-' + page}>
      <div className="ds-footer-logo"><img src={LOGO} alt="LPA" data-testid={'au-footer-brand-' + page} /></div>
      <div className="ds-footer-text">
        <div className="url">For office locations visit <a href="https://www.lpalighting.com" data-testid={'au-footer-website-' + page}>www.lpalighting.com</a></div>
        <div className="disclaimer" data-testid={'au-footer-disclaimer-' + page}>{DOCUMENT_DISCLAIMER}</div>
      </div>
    </div>
  );
}
